import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  OnChanges,
  OnInit,
  output,
  SecurityContext,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalComponent } from '@appkit4/angular-components/modal';
import { SidebarItemListService } from '@shared/services/sidebar-item.service';
import { RichEdit } from 'devexpress-richedit';
import { DataMode } from '../../helpers/enums/data-mode.enum';
import { EditorLoader } from '../../helpers/enums/editor-loader.enum';
import {
  buildInternalSectionUrl,
  INTERNAL_COUNTRY_SECTIONS,
  InternalCountrySection,
  InternalLinkInsertionRange,
} from '../../helpers/static/internal-country-sections';
import { HTMLCleanerService } from '../../services/html-cleaner.service';
import { RichEditorService } from '../../services/rich-editor.service';

@Component({
  standalone: false,
  selector: 'gstar-editor',
  templateUrl: './editor.component.html',
  styleUrl: './editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditorComponent implements OnInit, OnChanges {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly richEditor = inject(RichEditorService);
  private readonly htmlCleanerService = inject(HTMLCleanerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly sidebarItemService = inject(SidebarItemListService);

  content = input<SafeHtml | undefined>();
  title = input<string>('');
  mode = input<DataMode>(DataMode.edition);
  /** Adds the Country Information internal-link command to the editor ribbon. */
  allowInternalLinks = input<boolean>(false);
  update = output<RichEdit | undefined>();
  activeEditorState = signal(EditorLoader.isBuilding);
  editorStates: typeof EditorLoader = EditorLoader;

  container = viewChild<ElementRef<HTMLDivElement> | undefined>('editor');
  innerLinksPicker = viewChild<ElementRef<HTMLDivElement>>('innerLinksPicker');

  sanitizedHtml = signal<string>('');
  internalLinkInsertionRange = signal<InternalLinkInsertionRange | null>(null);

  countryId = computed(() => this.resolveCountryId());

  filteredInnerLinkSections = computed(() => {
    const currentTypeId = this.currentCountryInfoTypeId();

    return INTERNAL_COUNTRY_SECTIONS.filter(section => {
      return !(
        currentTypeId !== undefined &&
        section.countryInfoTypeId === currentTypeId
      );
    });
  });

  innerLinkGroups = computed(() => {
    const groups = new Map<string, InternalCountrySection[]>();
    for (const section of this.filteredInnerLinkSections()) {
      const items = groups.get(section.group) ?? [];
      items.push(section);
      groups.set(section.group, items);
    }
    return Array.from(groups.entries()).map(([name, sections]) => ({
      name,
      sections,
    }));
  });

  ngOnInit(): void {
    this.richEditor.richEditor$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((richEditor?: RichEdit) => {
        this.update.emit(richEditor);
      });

    this.richEditor.setInnerLinksEnabled(this.allowInternalLinks());

    if (this.allowInternalLinks()) {
      this.richEditor.innerLinksPicker$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((range: InternalLinkInsertionRange | null) => {
          this.internalLinkInsertionRange.set(
            this.mode() === DataMode.edition ? range : null
          );
        });

      this.richEditor.richEditorKeyDown$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((event: KeyboardEvent) => {
          if (event.key === 'Escape') {
            this.richEditor.closeInnerLinksPicker();
            return;
          }

          this.closeInnerLinksPickerOutside(event.target);
        });
    }

    this.sanitizeHtml(this.content());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['content'] && changes['content'].currentValue) {
      this.sanitizeHtml(changes['content'].currentValue);
    }

    if (changes['allowInternalLinks']) {
      this.richEditor.setInnerLinksEnabled(this.allowInternalLinks());
    }

    if (changes['mode'] && this.mode() !== DataMode.edition) {
      this.internalLinkInsertionRange.set(null);
    }
  }

  loadedHandler(_richEditor: RichEdit | undefined): void {
    this.activeEditorState.set(EditorLoader.isLoaded);
  }

  openUnsaveChangeWarningModal(
    isDisabledSaveButton?: boolean
  ): ComponentRef<ModalComponent> {
    return this.richEditor.openUnsaveChangeWarningModal(isDisabledSaveButton);
  }

  insertInnerLink(section: InternalCountrySection): void {
    const insertionRange = this.internalLinkInsertionRange();
    const countryId = this.countryId();
    if (
      !this.allowInternalLinks() ||
      !insertionRange ||
      countryId === undefined
    ) {
      return;
    }

    this.richEditor.insertInternalHyperlink(
      insertionRange,
      section.name,
      buildInternalSectionUrl(section, countryId)
    );
  }

  /**
   * Events to close the editor's inner links picker when interacting outside of it.
   * By default, once we click outside the inner links picker, it won't be closed.
   * With this function this will be close if we click outside of it manually.
   * @param event actionEvent
   */
  @HostListener('document:pointerdown', ['$event'])
  onDocumentPointerDown(event: PointerEvent): void {
    this.closeInnerLinksPickerOutside(event.target);
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.richEditor.closeInnerLinksPicker();
      return;
    }

    this.closeInnerLinksPickerOutside(event.target);
  }

  private currentCountryInfoTypeId(): number | undefined {
    const match = this.router.url.match(/\/general-information\/(\d+)/);
    if (!match) {
      return undefined;
    }

    const typeId = Number(match[1]);
    return Number.isFinite(typeId) ? typeId : undefined;
  }

  /**
   * Closes the inner links picker if the click occurred outside of it.
   * @param target The event target to check against the picker element.
   */
  private closeInnerLinksPickerOutside(target: EventTarget | null): void {
    const picker = this.innerLinksPicker()?.nativeElement;
    if (
      this.internalLinkInsertionRange() &&
      target instanceof Node &&
      !picker?.contains(target)
    ) {
      // The ribbon opens the picker on click, after pointerdown has already
      // fired, so the opening interaction cannot immediately dismiss it.
      this.richEditor.closeInnerLinksPicker();
    }
  }

  private resolveCountryId(): number | undefined {
    const fromRoute = this.route.snapshot.paramMap.get('countryId');
    if (fromRoute) {
      const parsed = Number(fromRoute);
      return Number.isFinite(parsed) ? parsed : undefined;
    }

    const fromUrl = this.router.url.match(/\/country\/(\d+)/);
    if (fromUrl) {
      const parsed = Number(fromUrl[1]);
      return Number.isFinite(parsed) ? parsed : undefined;
    }

    const selectedCountryId =
      this.sidebarItemService.selectedCountry()?.investmentJurisdictionId;
    return selectedCountryId ?? undefined;
  }

  private sanitizeHtml(content: SafeHtml | undefined): void {
    const sanitizedValue: string = this.parseToString(content);
    this.sanitizedHtml.set(
      sanitizedValue ? this.htmlCleanerService.clear(sanitizedValue) : ''
    );
  }

  private parseToString(safeValue: SafeHtml | undefined): string {
    return safeValue
      ? (this.sanitizer.sanitize(SecurityContext.HTML, safeValue) ?? '')
      : '';
  }
}
